import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RelationshipStatusSubmission } from '../../../../../models/submissions/relationship-status-submission';
import { BehaviorSubject } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { RelationshipStatusUtils } from '../../../../../utils/relationship-status-utils';
import { RelationshipStatusSubmissionDetailsUtils } from '../../../../../utils/relationship-status-submission-details-utils';

@Component({
  selector: 'app-spouse-contact-details-changes',
  templateUrl: './spouse-contact-details-changes.component.html',
  styleUrls: ['./spouse-contact-details-changes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpouseContactDetailsChangesComponent implements OnChanges {
  @Input() submission!: RelationshipStatusSubmission;

  private _submission = new BehaviorSubject<RelationshipStatusSubmission | null>(null);
  public submission$ = this._submission.pipe(filter((sub): sub is RelationshipStatusSubmission => sub !== null));

  public submissionInfoItems$ = this.submission$.pipe(
    map(sub => this.createSubmissionInfoItemArrays(sub)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public previousInfo$ = this.submissionInfoItems$.pipe(map(info => info.previous));
  public newInfo$ = this.submissionInfoItems$.pipe(map(info => info.new));

  public previousInfoDisplayString$ = this.submission$.pipe(
    map(sub => {
      if (!sub.previous || !RelationshipStatusUtils.isPartneredStatus(sub.previous.relationshipStatusId)) {
        return $localize`Not Applicable`;
      }
      return '';
    })
  );

  public newInfoDisplayString$ = this.submission$.pipe(
    map(sub => {
      if (!RelationshipStatusUtils.isPartneredStatus(sub.new.relationshipStatusId)) {
        return $localize`Not Applicable`;
      }
      if (
        !RelationshipStatusSubmissionDetailsUtils.spouseContactInfoChanged(
          sub?.previous?.spouse ?? null,
          sub.new.spouse
        )
      ) {
        return $localize`No Changes`;
      }
      return '';
    })
  );

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.submission) {
      this._submission.next(this.submission);
    }
  }

  private createSubmissionInfoItemArrays(submission: RelationshipStatusSubmission) {
    const previousData = submission.previous;
    const newData = submission.new;

    const spouseExistenceChanged = !!previousData?.spouse !== !!newData?.spouse;

    const items = [
      {
        label: $localize`Email Address`,
        prev: previousData?.spouse?.email ?? '',
        new: newData?.spouse?.email ?? ''
      },
      {
        label: $localize`Phone Number`,
        prev: previousData?.spouse?.phoneNumber ?? '',
        new: newData?.spouse?.phoneNumber ?? ''
      },
      {
        label: $localize`Country`,
        prev: previousData?.spouse?.address?.countryName ?? '',
        new: newData?.spouse?.address?.countryName ?? ''
      },
      {
        label: $localize`Address Line 1`,
        prev: previousData?.spouse?.address?.addressLine1 ?? '',
        new: newData?.spouse?.address?.addressLine1 ?? ''
      },
      {
        label: $localize`Address Line 2`,
        prev: previousData?.spouse?.address?.addressLine2 ?? '',
        new: newData?.spouse?.address?.addressLine2 ?? ''
      },
      {
        label: $localize`Address Line 3`,
        prev: previousData?.spouse?.address?.addressLine3 ?? '',
        new: newData?.spouse?.address?.addressLine3 ?? ''
      },
      {
        label: $localize`City/Town`,
        prev: previousData?.spouse?.address?.city ?? '',
        new: newData?.spouse?.address?.city ?? ''
      },
      {
        label: $localize`Province/State/Region`,
        prev: previousData?.spouse?.address?.state?.name ?? '',
        new: newData?.spouse?.address?.state?.name ?? ''
      },
      {
        label: $localize`Postal Code/ZIP`,
        prev: previousData?.spouse?.address?.postalCode ?? '',
        new: newData?.spouse?.address?.postalCode ?? ''
      }
    ];

    return {
      previous: items.map(item => ({
        label: item.label,
        value: item.prev || '',
        hasChanged: item.prev !== item.new || spouseExistenceChanged
      })),
      new: items.map(item => ({
        label: item.label,
        value: item.new || '',
        hasChanged: item.prev !== item.new || spouseExistenceChanged
      }))
    };
  }
}
